<template>
  <div class="container mx-auto px-4 py-8">
    <h1 class="text-4xl font-bold mb-6">Contact Us</h1>
    <div class="grid md:grid-cols-2 gap-8">
      <div class="bg-card rounded-lg shadow-sm p-6">
        <h2 class="text-2xl font-semibold text-card-foreground mb-4">Get in Touch</h2>
        <form @submit.prevent="handleSubmit" class="space-y-4">
          <div>
            <label class="block text-foreground/80 mb-2" for="name">Name</label>
            <input
              type="text"
              id="name"
              v-model="form.name"
              class="w-full px-4 py-2 rounded-lg border border-input bg-background text-foreground"
              required
            >
          </div>
          <div>
            <label class="block text-foreground/80 mb-2" for="email">Email</label>
            <input
              type="email"
              id="email"
              v-model="form.email"
              class="w-full px-4 py-2 rounded-lg border border-input bg-background text-foreground"
              required
            >
          </div>
          <div>
            <label class="block text-foreground/80 mb-2" for="message">Message</label>
            <textarea
              id="message"
              v-model="form.message"
              rows="4"
              class="w-full px-4 py-2 rounded-lg border border-input bg-background text-foreground"
              required
            ></textarea>
          </div>
          <button
            type="submit"
            class="w-full px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition"
            :disabled="isSubmitting"
          >
            <span v-if="isSubmitting">Sending...</span>
            <span v-else>Send Message</span>
          </button>
          <div v-if="formStatus.message" :class="[
            'p-3 rounded text-center',
            formStatus.success ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
          ]">
            {{ formStatus.message }}
          </div>
        </form>
      </div>
      <div class="bg-card rounded-lg shadow-sm p-6">
        <h2 class="text-2xl font-semibold text-card-foreground mb-4">Contact Information</h2>
        <div class="space-y-4 text-foreground/80">
          <p>
            <strong>Address:</strong><br>
            {{ siteStore.showroom_address || '123 Transport Street, Logistics City, ST 12345' }}
          </p>
          <p>
            <strong>Phone:</strong><br>
            <a :href="`tel:${siteStore.phone}`" class="hover:text-primary transition-colors">
              {{ siteStore.phone || '(555) 123-4567' }}
            </a>
          </p>
          <p>
            <strong>Email:</strong><br>
            <a href="mailto:info@company.com" class="hover:text-primary transition-colors">
              info@company.com
            </a>
          </p>
          <div v-if="hasDepartments">
            <h3 class="text-xl font-medium mt-6 mb-3">Departments</h3>
            <div v-if="siteStore.departments.sales.phone" class="mb-3">
              <p class="font-medium">Sales</p>
              <a :href="`tel:${siteStore.departments.sales.phone}`" class="hover:text-primary transition-colors">
                {{ siteStore.departments.sales.phone }}
              </a>
            </div>
            <div v-if="siteStore.departments.service.phone" class="mb-3">
              <p class="font-medium">Service</p>
              <a :href="`tel:${siteStore.departments.service.phone}`" class="hover:text-primary transition-colors">
                {{ siteStore.departments.service.phone }}
              </a>
            </div>
            <div v-if="siteStore.departments.parts.phone">
              <p class="font-medium">Parts</p>
              <a :href="`tel:${siteStore.departments.parts.phone}`" class="hover:text-primary transition-colors">
                {{ siteStore.departments.parts.phone }}
              </a>
            </div>
          </div>
          <div v-if="hasBusinessHours">
            <h3 class="text-xl font-medium mt-6 mb-3">Business Hours</h3>
            <div v-if="siteStore.departments.sales.trading?.notice?.length">
              <p class="font-medium">Sales</p>
              <div v-for="(notice, index) in siteStore.departments.sales.trading.notice" :key="index">
                <p>{{ notice.heading }}: {{ notice.start }} - {{ notice.end }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Map Section -->
    <div class="mt-8 bg-card rounded-lg shadow-sm p-6">
      <h2 class="text-2xl font-semibold text-card-foreground mb-4">Find Us</h2>
      <div class="aspect-video w-full rounded-lg overflow-hidden">
        <iframe 
          src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d12540.462791353077!2d146.4989013!3d-38.2072105!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b29180aa76727f3%3A0x5c82340aceb19db!2sTraralgon%20Subaru!5e0!3m2!1sen!2sau!4v1683501448897!5m2!1sen!2sau" 
          width="100%" 
          height="100%" 
          style="border:0;" 
          allowfullscreen 
          loading="lazy" 
          referrerpolicy="no-referrer-when-downgrade">
        </iframe>
      </div>
      <div class="mt-4 flex justify-end">
        <a 
          href="https://goo.gl/maps/8ZjdqL8Ug7ZQu5Lw8" 
          target="_blank" 
          rel="noopener noreferrer"
          class="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition"
        >
          Get Directions
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSiteStore } from '~/stores/site'

// Get the site store
const siteStore = useSiteStore()

// Form state
const form = reactive({
  name: '',
  email: '',
  message: ''
})

const isSubmitting = ref(false)
const formStatus = reactive({
  success: false,
  message: ''
})

// Computed properties for conditional rendering
const hasDepartments = computed(() => {
  return siteStore.departments.sales.phone || 
         siteStore.departments.service.phone || 
         siteStore.departments.parts.phone
})

const hasBusinessHours = computed(() => {
  return siteStore.departments.sales.trading?.notice?.length > 0
})

// Form submission handler
const handleSubmit = async () => {
  isSubmitting.value = true
  formStatus.message = ''
  
  try {
    // Call the server API route instead of simulating
    const response = await $fetch('/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json' // Explicitly set header
      },
      body: JSON.stringify(form) // Explicitly stringify the body
    })
    
    // Assuming the API returns { success: true, message: '...' }
    if ((response as any).success) {
      formStatus.success = true
      formStatus.message = (response as any).message || 'Thank you for your message! We will get back to you soon.'
      
      // Reset form
      form.name = ''
      form.email = ''
      form.message = ''
    } else {
      // Handle potential server-side errors not caught by catch block
      formStatus.success = false
      formStatus.message = (response as any).message || 'An unexpected error occurred on the server.'
    }

  } catch (error: any) { // Add type annotation for error
    // Error message
    formStatus.success = false
    // Check if the error object has a specific message from the API
    formStatus.message = error.data?.message || error.statusMessage || 'Sorry, there was an error sending your message. Please try again.'
    console.error('Form submission error:', error)
  } finally {
    isSubmitting.value = false
  }
}

// Set SEO metadata for the contact page
useSeoMeta({
  title: `Contact Us | ${siteStore.name}`,
  description: `Get in touch with ${siteStore.name} for any inquiries or support.`,
  ogTitle: `Contact ${siteStore.name}`,
  ogDescription: `Contact information and form for ${siteStore.name}.`
})
</script>